// @file Whiteboard Card Templates store.
import { captureFetchException, captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { getLinkingFromShape } from '@@/bits/whiteboard_linking'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { WhiteboardCardTemplatesApi } from '@@/surface/api/whiteboard_card_templates'
import type { WhiteboardCardTemplate } from '@@/types'
import { omit, pick, shuffle } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { JsonObject } from 'tldraw'
import { computed, ref, shallowRef } from 'vue'

interface CardTemplateItem {
  id: string
  name: string
  category: string
  thumbnailUrl: string
  previewUrl: string
  searchKeywords: string[]
}

export const useWhiteboardCardTemplatesStore = defineStore('whiteboardCardTemplates', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const whiteboardPanelsStore = useWhiteboardPanelsStore()

  // #region Suggested card templates row
  const xSuggestedCardTemplatesRow = ref(false)

  const canShowSuggestedCardTemplates = computed<boolean>(() => {
    // Only show suggested card templates on certain backgrounds.
    const currentFrameBackground =
      whiteboardStore.currentFrameId != null
        ? whiteboardFrameBackgroundStore.displayedFrameBackgrounds[whiteboardStore.currentFrameId]?.id
        : null
    const canShowOnCurrentBackground =
      currentFrameBackground != null
        ? ['Plain', 'Ruled', 'DotGrid', 'Grid', 'Frosted'].includes(currentFrameBackground)
        : true
    return (
      isAppUsing('sandboxCardTemplates') &&
      whiteboardModeStore.isDrawMode &&
      !whiteboardFrameBackgroundStore.isPreviewingFrameBackgrounds &&
      canShowOnCurrentBackground &&
      !whiteboardPanelsStore.xCardTemplatesPanel &&
      previewCardTemplateUrl.value == null &&
      xSuggestedCardTemplatesRow.value
    )
  })
  // #endregion

  // #region Card template list
  const cardTemplates = shallowRef<CardTemplateItem[]>([])
  const cardTemplatesFetchState = ref<'idle' | 'fetching' | 'success' | 'error'>('idle')
  const cardTemplateSnapshotsById = shallowRef<Record<string, WhiteboardCardTemplate['page_snapshot']>>({})

  const suggestedCardTemplates = computed<CardTemplateItem[]>(() => {
    return shuffle([...cardTemplates.value]).slice(0, 8)
  })

  const updateCardTemplates = (rawCardTemplates: WhiteboardCardTemplate[]): void => {
    cardTemplates.value = rawCardTemplates.map((template) => ({
      id: template.id,
      name: template.translated_name,
      category: template.translated_category,
      thumbnailUrl: template.thumbnail_url,
      previewUrl: template.preview_url,
      searchKeywords: template.translated_search_keywords,
    }))
    cardTemplateSnapshotsById.value = rawCardTemplates.reduce((acc, template) => {
      acc[template.id] = template.page_snapshot
      return acc
    }, {})
  }

  const fetchAndSaveCardTemplatesToStore = async (): Promise<void> => {
    if (['fetching', 'success'].includes(cardTemplatesFetchState.value)) return

    try {
      cardTemplatesFetchState.value = 'fetching'
      const rawCardTemplates = await WhiteboardCardTemplatesApi.fetch()
      updateCardTemplates(rawCardTemplates)
      cardTemplatesFetchState.value = 'success'
    } catch (e) {
      captureFetchException(e, { source: 'fetchAndSaveCardTemplatesToStore' })
      cardTemplatesFetchState.value = 'error'
    }
  }
  // #endregion

  // #region Preview card template
  const previewCardTemplateId = ref<string | null>(null)

  const previewCardTemplateUrl = computed<string | null>(() => {
    if (previewCardTemplateId.value == null) return null
    return cardTemplates.value.find((template) => template.id === previewCardTemplateId.value)?.previewUrl ?? null
  })

  const setPreviewTemplate = (templateId: string): void => {
    previewCardTemplateId.value = templateId
  }

  const unsetPreviewTemplate = (): void => {
    previewCardTemplateId.value = null
  }
  // #endregion

  // #region Apply card template
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  /**
   * Applies card template to the current card.
   * If `templateId` is not provided, it will use the current preview template.
   * If there is no preview template, it will do nothing.
   */
  const applyCardTemplate = async (templateId?: CardTemplateItem['id']): Promise<void> => {
    const editor = getEditorObject()
    if (editor == null) return

    // Double check that the current page doesn't have any shapes before applying
    if (editor.getCurrentPageShapeIds().size > 0) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Something went wrong. Cannot apply card template to a page with existing objects.'),
      })
      captureMessage('[WhiteboardCardTemplates] Cannot apply template to a page with existing objects', {
        context: {
          wallId: surfaceStore.wallId,
          pageId: editor.getCurrentPageId(),
        },
      })
      return
    }

    const chosenTemplateId = templateId ?? previewCardTemplateId.value
    if (chosenTemplateId == null) {
      return
    }

    const templateSnapshot = cardTemplateSnapshotsById.value[chosenTemplateId]
    if (templateSnapshot == null) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Something went wrong. The card template is not available.'),
      })
      captureMessage('[WhiteboardCardTemplates] The card template is not available', {
        context: {
          wallId: surfaceStore.wallId,
          templateId: chosenTemplateId,
        },
      })
      return
    }

    const currentPageId = editor.getCurrentPageId()
    editor.updatePage({
      id: currentPageId,
      meta: pick(templateSnapshot.page.meta, ['backgroundType', 'customBackgroundUrl']),
    })

    // Lazy import tldraw because this Pinia store is imported in whiteboard_fetch_initial_state.ts
    // We only want tldraw to be imported when it's actually used
    const { createShapeId } = await import('tldraw')
    templateSnapshot.shapes.forEach((shape) => {
      // We only keep URL linking for now
      const linking = getLinkingFromShape(shape)
      const meta: Pick<JsonObject, 'approved' | 'sound_effect' | 'visual_effect'> & { linking?: string } = pick(
        shape.meta,
        ['approved', 'sound_effect', 'visual_effect'],
      )
      if (linking?.targetType === 'url') {
        meta.linking = linking.toString()
      }
      editor.createShape({
        ...omit(shape, ['meta']),
        id: createShapeId(),
        meta,
      })
    })

    unsetPreviewTemplate()
    whiteboardPanelsStore.xCardTemplatesPanel = false
  }
  // #endregion

  return {
    xSuggestedCardTemplatesRow,
    canShowSuggestedCardTemplates,

    cardTemplates,
    cardTemplatesFetchState,
    cardTemplateSnapshotsById,
    suggestedCardTemplates,
    fetchAndSaveCardTemplatesToStore,

    previewCardTemplateUrl,
    setPreviewTemplate,
    unsetPreviewTemplate,

    applyCardTemplate,
  }
})

export type { CardTemplateItem }
